import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../authContext";

const HomePage = () => {
  const { user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState("");
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const response = await axios.get(
          `https://internsightbackend.onrender.com/api/applications?userId=${user.userId}`
        );
        setApplications(response.data);
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching applications.");
      }
    };

    if (user && user.userId) {
      fetchApplications();
    }
  }, [user]);

  if (!user || !user.userId) {
    return (
      <div style={styles.centeredContainer}>
        <h2 style={styles.authMessage}>Please log in to access your applications.</h2>
      </div>
    );
  }

  const handleSkillRecommendations = async (company, job) => {
    setLoading(true);
    setModalVisible(true);
    try {
      const response = await axios.post(
        "https://internsightbackend.onrender.com/api/applications/skillRecommendations",
        { userId: user.userId, company, job },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setOutput({ type: "skills", data: response.data.skillRecommendations });
    } catch (err) {
      setOutput({ type: "error", message: err.response?.data?.message || "Error fetching skill recommendations." });
    } finally {
      setLoading(false);
    }
  };

  const handleInterviewPrep = async (company, job) => {
    setLoading(true);
    setModalVisible(true);
    try {
      const response = await axios.post(
        "https://internsightbackend.onrender.com/api/applications/interviewPrep",
        { userId: user.userId, company, job },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setOutput({ type: "interview", data: response.data.recommendations });
    } catch (err) {
      setOutput({ type: "error", message: err.response?.data?.message || "Error fetching interview prep tips." });
    } finally {
      setLoading(false);
    }
  };

  const handleSkillAnalysis = async (company, job) => {
    setLoading(true);
    setModalVisible(true);
    try {
      const response = await axios.post(
        "https://internsightbackend.onrender.com/api/applications/skillAnalysis",
        { userId: user.userId, company, job },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      setOutput({ type: "skillAnalysis", data: response.data });
    } catch (err) {
      setOutput({ type: "error", message: err.response?.data?.message || "Error fetching skill analysis." });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteApplication = async (applicationId) => {
    try {
      await axios.delete(`https://internsightbackend.onrender.com/api/applications/${applicationId}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });
      setApplications(applications.filter((app) => app._id !== applicationId));
    } catch (err) {
      alert(err.response?.data?.message || "Error deleting application.");
    }
  };

  const truncateText = (text, maxLines = 4) => {
    const lineLimit = 100 * maxLines;
    return text.length > lineLimit ? `${text.substring(0, lineLimit)}...` : text;
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Your Job Applications</h2>
        <div style={styles.stats}>
          <span style={styles.statBadge}>{applications.length} {applications.length === 1 ? 'Application' : 'Applications'}</span>
        </div>
      </div>
      
      {error && <div style={styles.errorAlert}>{error}</div>}
      
      {applications.length > 0 ? (
        <div style={styles.applicationsGrid}>
          {applications.map((app) => (
            <div key={app._id} style={styles.applicationCard}>
              <div style={styles.cardHeader}>
                <h4 style={styles.companyName}>{app.company}</h4>
                <span style={styles.deadlineBadge}>
                  {new Date(app.deadline).toLocaleDateString()}
                </span>
              </div>
              
              <div style={styles.cardBody}>
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Position:</span>
                  <span style={styles.detailValue}>{app.job}</span>
                </div>
                
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Details:</span>
                  <p style={styles.detailText}>{truncateText(app.jobDetails)}</p>
                </div>
                
                <div style={styles.detailRow}>
                  <span style={styles.detailLabel}>Contact:</span>
                  <span style={styles.detailValue}>{app.contactInfo}</span>
                </div>
              </div>
              
              <div style={styles.cardFooter}>
                <button
                  onClick={() => handleSkillRecommendations(app.company, app.job)}
                  style={styles.buttonPrimary}
                >
                  Skill Recommendations
                </button>
                <button
                  onClick={() => handleInterviewPrep(app.company, app.job)}
                  style={styles.buttonSuccess}
                >
                  Interview Prep
                </button>
                <button
                  onClick={() => handleSkillAnalysis(app.company, app.job)}
                  style={styles.buttonSecondary}
                >
                  Skill Analysis
                </button>
                <button
                  onClick={() => handleDeleteApplication(app._id)}
                  style={styles.buttonDanger}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={styles.emptyState}>
          <div style={styles.emptyIllustration}></div>
          <h3 style={styles.emptyTitle}>No applications found</h3>
          <p style={styles.emptyText}>Add your first job application to get started</p>
        </div>
      )}

      {/* Modal for displaying output */}
      {modalVisible && (
        <div style={styles.modalBackdrop}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>
                {output?.type === "skills" && "Skill Recommendations"}
                {output?.type === "interview" && "Interview Preparation"}
                {output?.type === "skillAnalysis" && "Skill Analysis"}
              </h3>
              <button
                onClick={() => setModalVisible(false)}
                style={styles.closeButton}
              >
                &times;
              </button>
            </div>
            
            <div style={styles.modalContent}>
              {loading ? (
                <div style={styles.loaderContainer}>
                  <div style={styles.loader}></div>
                  <p style={styles.loadingText}>Loading recommendations...</p>
                </div>
              ) : (
                <>
                  {output?.type === "skills" && (
                    <div>
                      <h4 style={styles.sectionTitle}>Recommended Skills:</h4>
                      <div style={styles.recommendationsGrid}>
                        {output.data.map((skill, index) => (
                          <div key={index} style={styles.recommendationCard}>
                            <div style={styles.skillBadge}>{skill.skill}</div>
                            <div style={styles.recommendationDetails}>
                              <p style={styles.recommendationText}><strong>Course:</strong> {skill.courseName}</p>
                              <p style={styles.recommendationText}><strong>Provider:</strong> {skill.provider}</p>
                              <p style={styles.recommendationText}><strong>Description:</strong> {skill.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {output?.type === "interview" && (
                    <div>
                      <h4 style={styles.sectionTitle}>Interview Prep Tips:</h4>
                      <div style={styles.recommendationsGrid}>
                        {output.data.map((tip, index) => (
                          <div key={index} style={styles.recommendationCard}>
                            <div style={styles.recommendationDetails}>
                              <p style={styles.recommendationText}><strong>Platform:</strong> {tip.platformName}</p>
                              <p style={styles.recommendationText}><strong>URL:</strong> <a href={tip.url} target="_blank" rel="noopener noreferrer" style={styles.link}>{tip.url}</a></p>
                              <p style={styles.recommendationText}><strong>Focus:</strong> {tip.focus}</p>
                              <p style={styles.recommendationText}><strong>Category:</strong> {tip.category}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {output?.type === "skillAnalysis" && (
                    <div>
                      <h4 style={styles.sectionTitle}>Skill Analysis:</h4>
                      <div style={styles.analysisCard}>
                        <div style={styles.analysisSection}>
                          <h5 style={styles.analysisTitle}>Matched Skills</h5>
                          <div style={styles.skillsList}>
                            {output.data.matchedSkills.map((skill, index) => (
                              <span key={index} style={styles.skillMatch}>{skill}</span>
                            ))}
                          </div>
                        </div>
                        
                        <div style={styles.analysisSection}>
                          <h5 style={styles.analysisTitle}>Skills to Improve</h5>
                          <div style={styles.skillsList}>
                            {output.data.improveSkills.map((skill, index) => (
                              <span key={index} style={styles.skillImprove}>{skill}</span>
                            ))}
                          </div>
                        </div>
                        
                        <div style={styles.analysisSection}>
                          <h5 style={styles.analysisTitle}>Missing Skills</h5>
                          <div style={styles.skillsList}>
                            {output.data.missingSkills.map((skill, index) => (
                              <span key={index} style={styles.skillMissing}>{skill}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {output?.type === "error" && (
                    <div style={styles.errorAlert}>{output.message}</div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced styles object
const styles = {
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "30px 20px",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    color: "#333",
  },
  centeredContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "#f5f7fa",
  },
  authMessage: {
    color: "#555",
    fontSize: "1.5rem",
    fontWeight: "400",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
    paddingBottom: "15px",
    borderBottom: "1px solid #eaeaea",
  },
  title: {
    color: "#2c3e50",
    fontSize: "2rem",
    fontWeight: "600",
    margin: 0,
  },
  stats: {
    display: "flex",
    alignItems: "center",
  },
  statBadge: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    padding: "8px 15px",
    borderRadius: "20px",
    fontSize: "0.9rem",
    fontWeight: "600",
  },
  errorAlert: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "25px",
    borderLeft: "4px solid #ef5350",
  },
  applicationsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
    gap: "25px",
  },
  applicationCard: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    border: "1px solid #e0e0e0",
    ':hover': {
      transform: "translateY(-5px)",
      boxShadow: "0 6px 16px rgba(0, 0, 0, 0.12)",
    },
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #eaeaea",
  },
  companyName: {
    margin: 0,
    color: "#2c3e50",
    fontSize: "1.3rem",
    fontWeight: "600",
  },
  deadlineBadge: {
    backgroundColor: "#fff8e1",
    color: "#ff8f00",
    padding: "5px 10px",
    borderRadius: "12px",
    fontSize: "0.8rem",
    fontWeight: "600",
  },
  cardBody: {
    padding: "20px",
  },
  detailRow: {
    marginBottom: "15px",
  },
  detailLabel: {
    display: "block",
    color: "#7f8c8d",
    fontSize: "0.85rem",
    fontWeight: "600",
    marginBottom: "5px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  detailValue: {
    color: "#34495e",
    fontSize: "1rem",
    fontWeight: "500",
  },
  detailText: {
    color: "#34495e",
    fontSize: "0.95rem",
    lineHeight: "1.5",
    margin: "5px 0 0 0",
  },
  cardFooter: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    padding: "15px 20px",
    backgroundColor: "#f8f9fa",
    borderTop: "1px solid #eaeaea",
  },
  buttonPrimary: {
    background: "linear-gradient(135deg, #1976d2, #2196f3)",
    color: "#fff",
    padding: "10px 15px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: "600",
    transition: "all 0.2s ease",
    flex: "1 1 auto",
    minWidth: "120px",
    ':hover': {
      background: "linear-gradient(135deg, #1565c0, #1976d2)",
      boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
    },
  },
  buttonSuccess: {
    background: "linear-gradient(135deg, #388e3c, #4caf50)",
    color: "#fff",
    padding: "10px 15px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: "600",
    transition: "all 0.2s ease",
    flex: "1 1 auto",
    minWidth: "120px",
    ':hover': {
      background: "linear-gradient(135deg, #2e7d32, #388e3c)",
      boxShadow: "0 2px 8px rgba(56, 142, 60, 0.3)",
    },
  },
  buttonSecondary: {
    background: "linear-gradient(135deg, #455a64, #607d8b)",
    color: "#fff",
    padding: "10px 15px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: "600",
    transition: "all 0.2s ease",
    flex: "1 1 auto",
    minWidth: "120px",
    ':hover': {
      background: "linear-gradient(135deg, #37474f, #455a64)",
      boxShadow: "0 2px 8px rgba(69, 90, 100, 0.3)",
    },
  },
  buttonDanger: {
    background: "linear-gradient(135deg, #c62828, #d32f2f)",
    color: "#fff",
    padding: "10px 15px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.85rem",
    fontWeight: "600",
    transition: "all 0.2s ease",
    flex: "1 1 auto",
    minWidth: "120px",
    ':hover': {
      background: "linear-gradient(135deg, #b71c1c, #c62828)",
      boxShadow: "0 2px 8px rgba(211, 47, 47, 0.3)",
    },
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "50px 20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "10px",
    textAlign: "center",
  },
  emptyIllustration: {
    width: "150px",
    height: "150px",
    backgroundColor: "#e3f2fd",
    borderRadius: "50%",
    marginBottom: "25px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#1976d2",
    fontSize: "3rem",
  },
  emptyTitle: {
    color: "#2c3e50",
    fontSize: "1.5rem",
    fontWeight: "600",
    margin: "0 0 10px 0",
  },
  emptyText: {
    color: "#7f8c8d",
    fontSize: "1rem",
    margin: 0,
  },
  modalBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    backdropFilter: "blur(3px)",
  },
  modal: {
    background: "#fff",
    borderRadius: "12px",
    width: "85%",
    maxWidth: "800px",
    maxHeight: "80vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #eaeaea",
  },
  modalTitle: {
    margin: 0,
    color: "#2c3e50",
    fontSize: "1.4rem",
    fontWeight: "600",
  },
  closeButton: {
    background: "transparent",
    color: "#7f8c8d",
    border: "none",
    fontSize: "1.5rem",
    cursor: "pointer",
    padding: "5px 10px",
    borderRadius: "4px",
    transition: "all 0.2s ease",
    ':hover': {
      color: "#c62828",
      backgroundColor: "#ffebee",
    },
  },
  modalContent: {
    padding: "20px",
    overflowY: "auto",
    flex: 1,
  },
  loaderContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 0",
  },
  loader: {
    border: "4px solid #f3f3f3",
    borderTop: "4px solid #1976d2",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    animation: "spin 1s linear infinite",
    marginBottom: "15px",
  },
  loadingText: {
    color: "#7f8c8d",
    fontSize: "1rem",
  },
  sectionTitle: {
    color: "#2c3e50",
    fontSize: "1.2rem",
    fontWeight: "600",
    margin: "0 0 20px 0",
    paddingBottom: "10px",
    borderBottom: "1px solid #eaeaea",
  },
  recommendationsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "15px",
    marginTop: "15px",
  },
  recommendationCard: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "15px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    border: "1px solid #e0e0e0",
    transition: "all 0.2s ease",
    ':hover': {
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      transform: "translateY(-2px)",
    },
  },
  skillBadge: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    padding: "5px 10px",
    borderRadius: "12px",
    fontSize: "0.85rem",
    fontWeight: "600",
    display: "inline-block",
    marginBottom: "10px",
  },
  recommendationDetails: {
    marginTop: "10px",
  },
  recommendationText: {
    color: "#34495e",
    fontSize: "0.9rem",
    margin: "5px 0",
    lineHeight: "1.5",
  },
  link: {
    color: "#1976d2",
    textDecoration: "none",
    ':hover': {
      textDecoration: "underline",
    },
  },
  analysisCard: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    border: "1px solid #e0e0e0",
  },
  analysisSection: {
    marginBottom: "20px",
  },
  analysisTitle: {
    color: "#2c3e50",
    fontSize: "1rem",
    fontWeight: "600",
    margin: "0 0 10px 0",
  },
  skillsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  skillMatch: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    padding: "5px 10px",
    borderRadius: "12px",
    fontSize: "0.8rem",
    fontWeight: "500",
  },
  skillImprove: {
    backgroundColor: "#fff8e1",
    color: "#ff8f00",
    padding: "5px 10px",
    borderRadius: "12px",
    fontSize: "0.8rem",
    fontWeight: "500",
  },
  skillMissing: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    padding: "5px 10px",
    borderRadius: "12px",
    fontSize: "0.8rem",
    fontWeight: "500",
  },
};

export default HomePage;